import { useEffect, useRef } from 'react'

const HISTORY_CATCH_UP_POLL_MS = 1000
/** Backend history writes land within seconds; stop if one never does. */
const HISTORY_CATCH_UP_LIMIT_MS = 30_000

/**
 * Poll `refresh` until every turn id in `awaitingKeys` has caught up (its key
 * drops out), or `HISTORY_CATCH_UP_LIMIT_MS` elapses from the first time a
 * given key was seen.
 *
 * `awaitingKeys` is the pipe-joined `liveTurnsAwaitingHistory` string so the
 * caller controls memoization identity: nothing changes → effect doesn't
 * restart.
 */
export function useHistoryCatchUpPolling({
  isReady,
  awaitingKeys,
  refresh,
}: {
  isReady: boolean
  awaitingKeys: string
  refresh: () => Promise<void> | void
}): void {
  const startedAtByKey = useRef(new Map<string, number>())

  useEffect(() => {
    if (!isReady || !awaitingKeys) return
    const now = Date.now()
    const remainingMs = Math.max(
      ...awaitingKeys.split('|').map((turnKey) => {
        const startedAt = startedAtByKey.current.get(turnKey) ?? now
        startedAtByKey.current.set(turnKey, startedAt)
        return startedAt + HISTORY_CATCH_UP_LIMIT_MS - now
      })
    )
    if (remainingMs <= 0) return
    void refresh()
    const intervalId = window.setInterval(
      () => void refresh(),
      HISTORY_CATCH_UP_POLL_MS
    )
    const timeoutId = window.setTimeout(
      () => window.clearInterval(intervalId),
      remainingMs
    )
    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [awaitingKeys, isReady, refresh])
}
